import React from "react";
import { graphql } from "react-relay";

import { faFileTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faChevronDown,
	faPhotoVideo,
	faPlus,
	faTrashCan as farTrashAlt,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { captureException } from "@sentry/browser";
import cx from "classnames";
import { compact, debounce, forEach, isEqual, throttle } from "lodash";
import PhotoSwipe from "photoswipe";
import { Fragment, NodeType, Node as ProseNode, Slice } from "prosemirror-model";
import {
	EditorState,
	NodeSelection,
	Plugin,
	PluginKey,
	Selection,
	TextSelection,
	Transaction,
} from "prosemirror-state";
import { Transform } from "prosemirror-transform";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { el } from "redom";
import uuid, { v4 as uuid4 } from "uuid";

import type { FellowAttachment, FellowAttachmentRequest } from "~/attachments";
import AttachmentPreview from "~/attachments/AttachmentPreview";
import type { AttendeeAttrs } from "~/common/Attendees";
import { defaultThumbBounds, openLightbox, type PSUIOptions } from "~/imageViewer";
import { render } from "~/lib/dom";
import { warning } from "~/lib/prompts";
import { BlockActionMenu } from "~/lib/prose/blockActions";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext } from "~/lib/prose/plugins/context";
import { commitMutationPromise } from "~/lib/relayUtil";
import { blur, inVideocallMode, isZoom } from "~/lib/utils";
import { zoomOpenUrl } from "~/lib/zoom";
import { environment, relaySource } from "~/models";
import {
	getCommentMenuItem,
	getCopyLinkMenuItem,
	getCreatorMenuItem,
	getDeleteBlockMenu,
	getReactionMenuItem,
} from "~/notes/AssignableDropdown";
import toasty from "~/ui/toast";
import Tooltip from "~/ui/Tooltip";

import { NodeExtension } from "../../extension";
import { ImageStripItemDragSourceSingleton } from "../../plugins/dropTarget";
import { promptResponseAttrs } from "../../plugins/prompt";
import ReactNodeView, { type ReactNodeViewProps } from "../../react-node-view";
import type { getPosHandler } from "../../types";
import { nodeActive } from "../../utils";
import { retrieveIsCollapsed, saveIsCollapsed } from "../embed/embed";
import { ReactComponent as ReplaceIcon } from "./replace-icon.svg";

import type { imageStripAddMutation } from "~/__generated__/imageStripAddMutation.graphql";
import type { imageStripConfirmMutation } from "~/__generated__/imageStripConfirmMutation.graphql";

import styles from "./image-strip.module.css";

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

const MemoizedFontAwesomeIcon = React.memo(FontAwesomeIcon);
export const IMAGE_STRIP_MAX_CAPACITY = 3;
const DEFAULT_MIN_WIDTH_PCT = 0.25;
const DEFAULT_MIN_HEIGHT = 52;
const DEFAULT_MAX_HEIGHT = 320;
const FALLBACK_HEIGHT = 120; // if in any cases we failed to get the strip's width, we'll have to give them a height
const DEFAULT_HEIGHT_PCT = 0.33; // for non-image item, give it .33 so that 3 of them can take up the strip
type ResizePluginSpec = {
	decorationSet: DecorationSet;
};
type ResizePluginMeta =
	| {
			node?: ProseNode;
			getPos?: () => number;
			attachmentId: string;
			dimensions: { width: number | undefined; height: number | undefined };
			force?: boolean;
	  }
	| undefined;
const ResizePluginKey = new PluginKey("ImageStripResize");
const ItemActivePluginKey = new PluginKey("ImageStripItemActive");

export const insertImageStripTr = (tr: Transaction, userId: string, pos?: number) => {
	const newNode = (tr.doc.type.schema.nodes.image_strip as NodeType).createAndFill({
		blockId: uuid.v4(),
		creator: userId,
	});
	if (!newNode) return tr;
	tr.insert(typeof pos === "undefined" ? tr.selection.to : pos, newNode);
	return tr;
};

export const insertImageStrip = (pos?: number) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
	if (dispatch) {
		const context = getContext(state);
		const tr = insertImageStripTr(state.tr, context?.userId, pos);
		dispatch(tr);
	}
	return true;
};

export const addNewItemToStripTr = (insertPos: number, attachmentId: string, tr: Transaction) => {
	// the strip_pos might be mapped. So get a new one
	const stripStart = tr.doc.resolve(insertPos).start(1);
	const node = tr.doc.resolve(stripStart).node();
	if (node.type.name !== "image_strip") return tr;
	const isFirstItem = node.nodeSize === 2;
	const fileNode = (tr.doc.type.schema.nodes.image_strip_item_file as NodeType).createAndFill({
		attachmentId,
	});
	const captionNode = (tr.doc.type.schema.nodes.image_strip_item_caption as NodeType).createAndFill();
	if (!fileNode || !captionNode) return tr;
	const newItemNode = (tr.doc.type.schema.nodes.image_strip_item as NodeType).createAndFill({}, [
		fileNode,
		captionNode,
	]);
	if (!newItemNode) return tr;
	tr.insert(insertPos, newItemNode);
	if (isFirstItem) {
		tr.setMeta("addToHistory", false);
	}
	return tr;
};

export const addNewItemToStrip =
	(stripPos: number, attachmentId: string) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		if (!attachmentId) return false;
		const tr = addNewItemToStripTr(stripPos, attachmentId, state.tr);
		if (!tr.steps.length) return false;
		if (dispatch) {
			dispatch(tr);
		}
		return true;
	};

/**
 * @param node the strip node being checked
 * @param imageStripExt the view's image strip node extension
 * @returns isFull: whether the number of strip's items reach the maximum capacity
 * stripItemCount: how many items are there in the strip
 */
export const isStripFull = (node: ProseNode, imageStripExt: ImageStrip) => {
	const stripId = node.attrs["blockId"];
	const uploadingFiles = imageStripExt.uploadingFiles.get(stripId);
	const uploadingFilesCount = uploadingFiles?.size ?? 0;
	// if somehow we fail to get the childCount of the strip node we should prevent dropping into the strip
	const existItemsCount = node?.childCount ?? IMAGE_STRIP_MAX_CAPACITY;
	const stripItemCount = existItemsCount + uploadingFilesCount;
	return {
		isFull: stripItemCount >= IMAGE_STRIP_MAX_CAPACITY,
		stripItemCount,
	};
};

/**
 * The function inserts a fragment that represents a strip item to a position
 * @param insertPos the position the item should be inserted at
 * @param insertFragment the fragment of the strip item that's going to be inserted
 * @param tr transaction
 * @returns the transaction after inserting the item
 */
const insertItemTr = (insertPos: number, insertFragment: Fragment, tr: Transaction) => {
	tr.insert(insertPos, insertFragment);
	return tr;
};

/**
 * The function deletes a strip item by its position.
 * @param startPos the position the delete should start from
 * @param endPos the position the delete should end at
 * @param tr transaction
 * @returns the transaction after deleting the item
 */
const removeItemTr = (startPos: number, endPos: number, tr: Transaction) => {
	// since we are deleting the item after inserting it to the new position,
	// we need to get the mapped position after the first transaction
	const mappedStartPos = tr.mapping.map(startPos);
	const mappedEndPos = tr.mapping.map(endPos);
	tr.delete(mappedStartPos, mappedEndPos);
	return tr;
};

/**
 * The function set the selection to where the item is inserted at
 * @param insertPos the position the item is inserted at
 * @param tr transaction
 * @returns the transaction after selecting the item
 */
const focusItemTr = (insertPos: number, tr: Transaction) => {
	// instead of selecting the "image_strip_item" node, we are selecting the
	// "image_strip_item_file" node, which is the first child of the "item" node
	const itemFilePos = insertPos + 1;
	const newSelection = NodeSelection.create(tr.doc, itemFilePos);
	tr.setSelection(newSelection);
	return tr;
};

/**
 * The function moves a strip item to somewhere else, could be into another strip
 * or create a new strip, or just moving around inside the same strip
 * @param insertPos The position the item going to insert at
 * @param insertFragment The fragment of the item that's going to insert
 * @param delStartPos The start position of the original item, that needs to be delete after moving
 * @param delEndPos The end position of the original item
 * @returns void
 */
export const moveItemFromStrip =
	(insertPos: number, insertFragment: Fragment, delStartPos: number, delEndPos: number) =>
	(targetView: EditorView, sourceView: EditorView) => {
		if (targetView === sourceView) {
			const trAfterInsert = insertItemTr(insertPos, insertFragment, targetView.state.tr);
			const trAfterFocus = focusItemTr(insertPos, trAfterInsert);
			const trAfterRemove = removeItemTr(delStartPos, delEndPos, trAfterFocus);
			targetView.dispatch(trAfterRemove.setMeta(ResizePluginKey, true));
			return;
		}
		const trAfterInsert = insertItemTr(insertPos, insertFragment, targetView.state.tr);
		const trAfterFocus = focusItemTr(insertPos, trAfterInsert);
		targetView.dispatch(trAfterFocus.setMeta(ResizePluginKey, true));

		const trAfterRemove = removeItemTr(delStartPos, delEndPos, sourceView.state.tr);
		sourceView.dispatch(trAfterRemove.setMeta(ResizePluginKey, true));
		return;
	};

/**
 * The function check if any image strips has been changed between two state and if
 * a resize calculation is needed
 * @param oldState the state before the transactions are applied
 * @param newState the state after the transactions are applied
 * @returns boolean
 */
const isStripsNeedResize = (oldState: EditorState, newState: EditorState) => {
	const map: Map<
		string,
		{
			old: { node: ProseNode; index: number } | null;
			new: { node: ProseNode; index: number } | null;
		}
	> = new Map();
	const { doc: oldDoc } = oldState;
	const { doc: newDoc } = newState;
	let isStripsChanged = false;

	oldDoc.forEach((node, _offset, index) => {
		if (node.type.name === "image_strip") {
			map.set(node.attrs["blockId"], { old: { node, index }, new: null });
		}
	});
	newDoc.forEach((node, _offset, index) => {
		if (node.type.name === "image_strip") {
			const oldStrip = map.get(node.attrs["blockId"])?.old;
			if (oldStrip) {
				// A multiselect drop transaction involves an image strip will first remove the whole strip
				// and then insert it at another position, and the original decorations on that strip won't be
				// mapped through this kind of transaction. While on the local side we can manually trigger
				// a resize to recalculate its decorations, we need a way to handle this if it is from remote.
				// However if this kind of transaction is from remote, we can barely know it is of this kind,
				// because we don't have its metadata (which we have if it is from local side). So here we
				// have to consider the index changes of a strip, even the strip's content is not changed at all.
				if (oldStrip.index !== index) isStripsChanged = true;
				else map.set(node.attrs["blockId"], { old: oldStrip, new: { node, index } });
			} else {
				isStripsChanged = true; // new strip added
			}
		}
	});
	if (isStripsChanged) return true;

	for (const { old: oldStrip, new: newStrip } of map.values()) {
		// oldStrip will exist otherwise function will return before this line. If newStrip doesn't exist
		// means a strip is deleted which is fine and we can move on to check the next one
		if (!newStrip) continue;
		if (!oldStrip?.node.eq(newStrip.node)) return true;
	}
	return false;
};

const replaceItemInStrip =
	(itemPos: number, newAttachmentId: string) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const fileNode = state.doc.nodeAt(itemPos + 1);
		const captionNode = state.doc.nodeAt(itemPos + 2);
		if (fileNode?.type.name !== "image_strip_item_file" || captionNode?.type.name !== "image_strip_item_caption")
			return false;
		const tr = state.tr;
		tr.setNodeMarkup(itemPos + 1, undefined, { ...fileNode.attrs, attachmentId: newAttachmentId });
		tr.setSelection(Selection.atStart(tr.doc));
		if (dispatch) {
			dispatch(tr);
		}
		return true;
	};

export const isRangeInSingleCaption = (doc: ProseNode, from: number, to: number) => {
	const $from = doc.resolve(from);
	const possibleCaptionNode = $from.node(3);

	if (to === from) {
		return possibleCaptionNode?.type.name === "image_strip_item_caption";
	}
	const $to = doc.resolve(to);
	return possibleCaptionNode?.type.name === "image_strip_item_caption" && possibleCaptionNode === $to.node(3);
};

export const isImageStripInRange = (doc: ProseNode, from: number, to: number) => {
	let seenImageStrip = false;
	doc.nodesBetween(from, to, node => {
		if (node.type.name === "image_strip") {
			seenImageStrip = true;
		}
		return false;
	});
	return seenImageStrip;
};

const isToCollapse = (target: EventTarget | null) => {
	if (!target || !(target instanceof Element)) return false;
	let node = target as Element | null;
	while (node) {
		if (node && node.classList.contains(styles.imageStripTitle)) {
			return true;
		} else if (node && node.classList.contains(styles.imageStrip)) {
			return false;
		}
		node = node.parentElement;
	}
	return false;
};

/**
 * Determine if the selection is inside an image strip, and if is the same one as another node
 * @param selection The current selection
 * @param node if present, check if the selection is inside the same one node
 * @returns boolean
 */
const isSelectionInStrip = (selection: Selection, node?: ProseNode) => {
	const { $from } = selection;
	const ancestor = $from.node(1); // node at top level
	const isInStrip = ancestor && ancestor.type.name === "image_strip";
	if (node instanceof ProseNode) return isInStrip && ancestor === node;
	return isInStrip;
};

/**
 * Determine if the selection is on an image strip item file, and if is the same one as another node
 * @param selection The current selection
 * @param node if present, check if the selecting item file is the same one as it
 * @returns boolean
 */
const isSelectingItemFile = (selection: Selection, node?: ProseNode) => {
	const { $from, $to, empty } = selection;
	const { nodeAfter } = $from;
	const { nodeBefore } = $to;
	const isSelected = !empty && nodeAfter?.type.name === "image_strip_item_file" && nodeAfter === nodeBefore;
	if (node instanceof ProseNode) return isSelected && nodeAfter === node;
	return isSelected;
};

/**
 * Determine if the caption node of items under a strip should hide.
 * @param node the strip node
 * @param selection current selection
 * @returns boolean
 */
const shouldHideCaption = (node: ProseNode, selection: Selection) => {
	// The caption node under a strip should hide when ALL items has no caption (meaning the caption has no content),
	// except the selection is inside one of the items in the strip, in that case we need to render the
	// caption placeholder so the caption node must not be hide
	let noCaption = true;
	node.content.forEach(n => {
		if (n.lastChild && n.lastChild.type.name === "image_strip_item_caption") {
			noCaption = noCaption && n.lastChild.textBetween(0, n.lastChild.content.size, "\n").length === 0;
		}
	});
	const selectionInStrip = isSelectionInStrip(selection, node);
	// hide caption when all items in the strip have no caption and the selection is not inside the strip node
	return noCaption && !selectionInStrip;
};

const isExtremeRatio = (ratio: number) => {
	return ratio < 0.5 || ratio > 2;
};

const objectFitMode = (
	ratio: number,
	dimension: { width?: number; height?: number } | undefined,
	calculatedWidth: number,
	calculatedHeight: number,
	devicePixelRatio: number,
) => {
	// if the dimension doesn't exsit, it is a non-image file which we should skip it
	if (!dimension || !dimension.width || !dimension.height) return "";

	const width = Math.trunc(dimension.width);
	const height = Math.trunc(dimension.height);
	// adjusted dimensions width and height
	const adjustedWidth = Math.trunc(dimension.width / devicePixelRatio);
	const adjustedHeight = Math.trunc(dimension.height / devicePixelRatio);
	const truncedCalWidth = Math.trunc(calculatedWidth);
	const truncedCalHeight = Math.trunc(calculatedHeight);
	if (truncedCalWidth === adjustedWidth && truncedCalHeight === adjustedHeight) return "";
	const shouldNone =
		!isExtremeRatio(ratio) &&
		truncedCalWidth > width &&
		truncedCalHeight > height &&
		truncedCalHeight > adjustedHeight &&
		truncedCalWidth > adjustedWidth;
	if (shouldNone) return "none";
	const shouldContain =
		isExtremeRatio(ratio) ||
		(truncedCalWidth <= width && truncedCalHeight >= height) ||
		(truncedCalHeight <= height && truncedCalWidth >= width) ||
		(truncedCalWidth <= adjustedWidth && truncedCalHeight >= adjustedHeight) ||
		(truncedCalHeight <= adjustedHeight && truncedCalWidth >= adjustedWidth);
	if (shouldContain) return "contain";
	return "";
};

const AddMutation = graphql`
	mutation imageStripAddMutation($input: RequestAddAttachmentInput!) {
		requestAddAttachment(input: $input) {
			ok
			fields
			url

			attachment {
				id
				dimensions {
					width
					height
				}
			}

			errors {
				message
			}
		}
	}
`;

const ConfirmMutation = graphql`
	mutation imageStripConfirmMutation($input: ConfirmAddAttachmentInput!) {
		confirmAddAttachment(input: $input) {
			edge {
				node {
					id
					filename
					updatedAt
					url
					size
					filetype
					dimensions {
						width
						height
					}
				}
			}
		}
	}
`;

declare module "photoswipe" {
	interface Item {
		attachmentId: string | undefined;
		creator?: {
			fullName?: string;
			image?: string;
		};
		caption?: string;
	}
}

export class ImageStrip extends NodeExtension {
	uploadingFiles: Map<string, Map<string, File>> = new Map();
	uploadingPos: Map<string, number> = new Map();
	uploadingFilesPos: Map<string, string> = new Map(); // map uploading file to its position (tempId -> sessionId)
	// map uploading file to its dimension by strip
	uploadingFilesDimensions: Map<string, Map<File, { width: number; height: number }>> = new Map();
	// uploadingPos = 0;
	// This is to avoid flickering by enabling getting a preview url from attachment id.
	// TODO: when to clean this?
	uploadedFiles: Map<string, File> = new Map();
	selectedFields: File[] = [];

	lightbox?: {
		pswp: PhotoSwipe<PSUIOptions>;
		rootElement: HTMLDivElement;
		stripId: string;
	};

	focusedImageStripPos: number | undefined = undefined;
	hoveringImageStripPos: number | undefined = undefined;

	dimensionsInStrip: Map<string, FellowAttachment["dimensions"]>;
	dimensionsChanged = false;
	resizeObserver: ResizeObserver | undefined;
	stripWidth: number | undefined;
	devicePixelRatio: number;
	matchMediaQuery: MediaQueryList;

	static lastInputElement?: HTMLInputElement;
	get inputElement() {
		const newInputElement = document.createElement("input");
		newInputElement.type = "file";
		newInputElement.setAttribute("data-images-strip-upload", "true");
		newInputElement.multiple = true;
		newInputElement.style.display = "none";
		if (ImageStrip.lastInputElement) {
			document.body.replaceChild(newInputElement, ImageStrip.lastInputElement);
			ImageStrip.lastInputElement.remove();
		} else {
			document.body.appendChild(newInputElement);
		}
		ImageStrip.lastInputElement = newInputElement;
		return ImageStrip.lastInputElement;
	}

	constructor(
		options: {
			contentId?: string;
			disable?: boolean;
			disableWhenNotEditable?: boolean;
			hidePreview?: boolean;
			initialDimensions?: { [key: string]: { width: number | undefined; height: number | undefined } };

			// @ts-ignore
			requestAttachment?: FellowAttachmentRequest;
		} = {
			disable: false,
			disableWhenNotEditable: false,
			hidePreview: false,
			initialDimensions: {},
		},
	) {
		super(options);
		this.dimensionsInStrip = new Map<string, FellowAttachment["dimensions"]>();
		if (options.initialDimensions) {
			Object.entries(options.initialDimensions).forEach(([k, v]) => {
				this.setAttachmentDimensions(k, v);
			});
		}
		this.devicePixelRatio = window.devicePixelRatio ?? 1;
		this.matchMediaQuery = window.matchMedia(`(resolution: ${this.devicePixelRatio}dppx)`);
	}

	init() {
		this.getStripWidth();
		this.initResizeObserver();
		this.matchMediaQuery.addEventListener("change", this.onPixelRatioChange, { once: true });
	}

	destroy() {
		this.lightbox?.pswp.close();
		this.destroyResizeObserver();
		this.matchMediaQuery.removeEventListener("change", this.onPixelRatioChange);
	}

	get name() {
		return "image_strip";
	}

	view = (node: ProseNode, view: EditorView, getPos: getPosHandler, _decorations: readonly Decoration[]) => {
		const outerDOM = document.createElement("div");
		outerDOM.setAttribute("pmblock", "");
		outerDOM.setAttribute("data-block-id", node.attrs["blockId"]);
		outerDOM.setAttribute("data-block-type", "image_strip");
		outerDOM.setAttribute("data-creator", node.attrs["creator"]);
		outerDOM.className = cx(styles.imageStripWrapper, { previewHidden: this.options.hidePreview });

		const gridDOM = document.createElement("div");
		gridDOM.classList.add(cx(styles.imageStrip));
		if (retrieveIsCollapsed(node.attrs["blockId"])) {
			gridDOM.classList.add(styles.collapse);
		}
		if (shouldHideCaption(node, view.state.selection)) {
			gridDOM.classList.add(styles.hideCaption);
		}
		outerDOM.appendChild(gridDOM);

		const onClickCollapse = this.collapseStrip(gridDOM, node, getPos);
		const titleDom = this.renderStripTitle(node, onClickCollapse);
		if (titleDom instanceof HTMLElement) {
			gridDOM.appendChild(titleDom);
		}

		const contentDOM = document.createElement("div");
		const stripLayout = node.childCount > IMAGE_STRIP_MAX_CAPACITY ? styles.imageStripGrid : styles.imageStripFlex;
		contentDOM.className = cx(stripLayout, { [styles.imageStripGridEmpty]: node.childCount === 0 });
		contentDOM.setAttribute("pmcontent", "");
		gridDOM.appendChild(contentDOM);

		const context = getContext(view.state);
		if (typeof getPos === "boolean") throw new Error("getPos is a bool for node");
		if (blockActionsEnabled(view.state)) {
			const activeParticipants: AttendeeAttrs[] = context.participants.filter(
				(p: AttendeeAttrs) => p.user?.isActive || p.invitable,
			);
			const user = context?.participants?.find((u: AttendeeAttrs) => u.id === context?.userId);
			const blockActions = new BlockActionMenu({
				node,
				view,
				menuSections: () => [
					{ children: [this.getUploadImageMenu({ view, getPos, text: "Upload another image" })] },
					{
						children: context.isMeetingModal
							? [getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view })]
							: !context.isPrivateNote
								? [
										getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view }),
										getCommentMenuItem({ view, blockKey: node.attrs.blockId }),
									]
								: [],
					},
					{
						children: [
							...(context?.noteId
								? [getCopyLinkMenuItem({ noteId: context.noteId, blockKey: node.attrs.blockId })]
								: []),
							getDeleteBlockMenu({ view, getPos, text: "Delete strip" }),
						],
					},
					{
						children: [
							getCreatorMenuItem({
								creator: activeParticipants.find(u => u.id === node.attrs.creator) ?? null,
								user,
							}),
						],
					},
				],
				triggers: [],
			});
			outerDOM.appendChild(blockActions.el);
		}

		return {
			dom: outerDOM,
			contentDOM,
			update: (newNode: ProseNode) => {
				if (node !== newNode) return false;
				if (newNode.childCount !== node.childCount) {
					const newTitleDom = this.renderStripTitle(newNode, onClickCollapse);
					if (newTitleDom instanceof HTMLElement) {
						titleDom instanceof HTMLElement && gridDOM.replaceChild(newTitleDom, titleDom);
						titleDom === null && gridDOM.insertBefore(newTitleDom, contentDOM);
					} else if (titleDom instanceof HTMLElement) {
						titleDom.remove();
					}
				}
				if (shouldHideCaption(newNode, view.state.selection)) {
					gridDOM.classList.add(styles.hideCaption);
				} else {
					gridDOM.classList.remove(styles.hideCaption);
				}
				return true;
			},
			ignoreMutation: (
				record:
					| MutationRecord
					| {
							type: "selection";
							target: Element;
					  },
			) => {
				if (record.type === "selection" && contentDOM === record.target) {
					return false;
				}
				if (record.type !== "selection" && !contentDOM?.contains(record.target)) {
					return true;
				}
				if (record.type == "characterData" || record.type == "childList") {
					return false;
				}
				return true;
			},
		};
	};

	get node(): NodeExtension["node"] {
		return {
			content: "image_strip_item*",
			defining: true,
			group: "block",
			attrs: {
				blockId: { default: null },
				creator: { default: null },
				...promptResponseAttrs,
			},
			parseDOM: [
				{
					tag: `[data-block-type="${this.name}"]`,
					getAttrs: dom =>
						dom instanceof Element
							? { blockId: dom.getAttribute("data-block-id"), creator: dom.getAttribute("data-creator") }
							: null,
				},
			],
			toDOM: node => [
				"div",
				{
					class: cx(styles.imageStripWrapper, { previewHidden: this.options.hidePreview }),
					"data-block-id": node.attrs.blockId,
					"data-creator": node.attrs.creator,
					"data-block-type": node.type.name,
				},
				[
					"div",
					{
						class: cx(styles.imageStrip),
					},
					[
						"div",
						{
							class: cx(styles.imageStripGrid, { [styles.imageStripGridEmpty]: node.childCount === 0 }),
						},
						0,
					],
				],
			],
			destroy: () => {
				this.lightbox?.pswp.close();
			},
		};
	}

	collapseStrip = (stripDOM: HTMLElement, node: ProseNode, getPos: getPosHandler) => (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		const isStripCollapsed = stripDOM.classList.contains(styles.collapse);
		if (isStripCollapsed) {
			stripDOM.classList.remove(styles.collapse);
			saveIsCollapsed(node.attrs["blockId"], false);
		} else {
			stripDOM.classList.add(styles.collapse);
			saveIsCollapsed(node.attrs["blockId"], true);
		}

		// handle selection after collapse
		if (!this.editorView) return;
		const {
			editorView: {
				state: { tr, selection, doc },
				dispatch,
			},
		} = this;

		// if selection is inside the strip node when trying to collapse, search for a cursor forward after the strip
		if (isSelectionInStrip(selection, node) && !isStripCollapsed) {
			if (typeof getPos === "function") {
				const startPos = doc.resolve(getPos() + node.nodeSize);
				const newSelection = TextSelection.near(startPos);
				tr.setSelection(newSelection);
				dispatch(tr);
				this.editorView.focus();
			}
			return;
		}

		// manually blur out the editor if the activeElement is not inside it when collapsing, otherwise prose will reset
		// the editor's focus even if the focus wasn't inside it at the first place
		// noted that even if an "image_strip_item_file" node is being selected, it doesn't count as the activeElement,
		// so the following few lines must go after checking if the selection is inside a strip ("isSelectionInStrip")
		if (!this.editorView.dom.contains(document.activeElement)) {
			blur();
			return;
		}

		// if the selection is not inside the strip and the editor is being focused,
		// we want to keep the current selection after collpasing/expanding
		tr.setSelection(selection);
		dispatch(tr);
		this.editorView.focus();
		return;
	};

	renderStripTitle = (node: ProseNode, onClickTitle: (event: MouseEvent) => void) => {
		if (node.childCount <= 0) return null;
		// Always show the file count, regardless of captions
		const textToShow = node.childCount === 1 ? "1 file" : `${node.childCount} files`;
		const titleText = el("span", { class: cx(styles.imageStripTitleText), contenteditable: "false" }, textToShow);
		const titleButton = el(
			"span",
			{ class: cx(styles.imageStripTitleTrigger), contenteditable: "false" },
			render(<FontAwesomeIcon icon={faChevronDown} />),
		);
		const title = el("div", { class: styles.imageStripTitle, contenteditable: "false" }, [titleText, titleButton]);
		title.addEventListener("click", onClickTitle);
		const wrapper = el("div", { class: styles.imageStripTitleWrapper, contenteditable: "false" }, title);
		// prevent default behaviour for mousedown event here to stop prose from setting the focus into the editor
		// when click to collapse/expand, otherwise we won't know whether the focus was inside the editor or not before the click,
		// and so we can't determine where we should set the selection after collapsing/expanding the strip (i.e. if the
		// editor wasn't focused before the click, we should keep that after the click)
		wrapper.addEventListener("mousedown", e => {
			e.preventDefault();
		});
		return wrapper;
	};

	getAttachment = async (id: string) => {
		const imageStripExt = this.editorView?.extensions.get("image_strip") as ImageStrip;
		const { requestAttachment } = imageStripExt.options;
		const attachmentRequest = new Promise<FellowAttachment | undefined>(resolve => {
			if (requestAttachment) {
				(async () => {
					resolve(await requestAttachment(id));
				})();
			} else {
				const relayRecord = relaySource.get(id) as
					| {
							filetype: string;
							filename: string;
							url: string;
							id: string;
							size: number;
							updatedAt: string;
							creator: { __ref: string };
							dimensions: { __ref: string } | undefined;
					  }
					| undefined;

				if (typeof relayRecord?.filetype !== "string") return;
				const { url } = relayRecord;

				/* eslint-disable no-underscore-dangle */
				const dimensions = relayRecord.dimensions?.__ref
					? (relaySource.get(relayRecord.dimensions?.__ref) as FellowAttachment["dimensions"])
					: undefined;
				const creator = relayRecord.creator?.__ref
					? (relaySource.get(relayRecord.creator?.__ref) as FellowAttachment["creator"])
					: undefined;
				/* eslint-enable no-underscore-dangle */
				const attachment = Object.assign({}, relayRecord, { url, creator, dimensions });
				resolve(attachment);
			}
		});
		return await attachmentRequest;
	};

	generateLightboxProps = async (pos: number): Promise<{ items: PhotoSwipe.Item[]; initialIndex: number }> => {
		const view = this.editorView;

		if (!view) throw "openLightbox couldn't find editorView";
		const $pos = view.state.doc.resolve(pos),
			node = $pos.node(1),
			targetNode = $pos.node(2);
		if (!node) throw "openLightbox found no block at provided position";
		if (typeof node.attrs.blockId !== "string" || node.type.name !== this.name) {
			throw `openLightbox found ${node.type.name} at provided position - expected ${this.name}`;
		}

		let attachments: [attachment: Promise<FellowAttachment | undefined>, caption?: string][] = [],
			slides: PhotoSwipe.Item[] = [],
			initialIndex = 0;

		node.content.descendants((child, _pos, parent) => {
			if (child.type.name === "image_strip_item_file") {
				if (typeof child.attrs.attachmentId === "string") {
					/* TODO: This data should probably be grabbed from note context passed from React, but that's pretty unstable
					 * as of now (2020-10) */
					attachments.push([this.getAttachment(child.attrs.attachmentId), parent?.child(1).textContent]);
				}
				return false;
			} else {
				return true;
			}
		});

		for (const [promise, caption] of attachments) {
			const attachment = await promise;
			if (attachment?.filetype.startsWith("image") && typeof attachment.url === "string") {
				const a: PhotoSwipe.Item = {
					src: attachment.url,
					msrc: attachment.url, // TODO: smaller thumbnail urls
					attachmentId: typeof attachment.id === "string" ? attachment.id : undefined,
					...(attachment.dimensions
						? { w: attachment.dimensions.width, h: attachment.dimensions.height }
						: { autoSize: true }),
					caption,
					creator: attachment.creator,
				};
				slides.push(a);

				if (targetNode.firstChild?.attrs.attachmentId === attachment.id) {
					initialIndex = slides.length - 1;
				}
			}
		}

		return { items: slides, initialIndex };
	};

	/**
	 * this function handles updating the caption inside the image viewer
	 * @param item the item currently showing
	 * @param captionWrapperElement the wrapper element of the caption
	 * @returns whether updated the caption
	 */
	addCaptionHTMLFn = (item: PhotoSwipe.Item, captionWrapperElement: Element) => {
		// the actual caption is another element inside its wrapper
		const caption = captionWrapperElement.getElementsByClassName("pswp__caption__center")[0] as HTMLDivElement;
		if (item.caption) {
			caption.innerHTML = item.caption;
		} else {
			caption.innerHTML = "";
		}
		return true;
	};

	/**
	 * this function handles updating the creator infos inside the image viewer
	 * @param pswp the PhotoSwipper instance
	 * @returns undefined
	 */
	afterChange = (pswp: PhotoSwipe<PSUIOptions>) => {
		const currentItem = pswp.currItem;
		const uiElement = pswp.container.nextElementSibling as HTMLDivElement;
		// set avatar and creator name
		if (currentItem.creator?.image && currentItem.creator?.fullName) {
			const imgEl = uiElement.querySelector("[data-pswp-avatar-image]") as HTMLImageElement;
			const nameEl = uiElement.querySelector("[data-pswp-creator-name]") as HTMLDivElement;
			imgEl.setAttribute("src", currentItem.creator?.image || "");
			nameEl.innerHTML = `Added by ${currentItem.creator?.fullName ?? ""}`;
		}
	};

	openImageViewer = async (pos: number) => {
		const view = this.editorView;
		if (!view) throw "openLightbox couldn't find editorView";
		const $pos = view.state.doc.resolve(pos),
			node = $pos.node(1);
		const stripId = node.attrs.blockId;
		const { items, initialIndex } = await this.generateLightboxProps(pos);

		const getThumbBoundsFn = (index: number) => {
			const { attachmentId } = items[index];
			if (attachmentId === undefined) return defaultThumbBounds();
			const thumb = document.querySelector(`[data-block-id="${stripId}"] [data-attachment-id="${attachmentId}"]`);
			if (!thumb) return defaultThumbBounds();
			const rect = thumb.getBoundingClientRect();
			return {
				x: rect.left,
				y: rect.top,
				w: rect.width,
			};
		};
		try {
			const res = await openLightbox({
				items,
				initialIndex,
				getThumbBoundsFn,
				addCaptionHTMLFn: this.addCaptionHTMLFn,
				onDelete: this.onTrashItem,
				onDestroy: this.onLightboxDestroyed,
				afterChange: this.afterChange,
			});
			if (res) {
				this.lightbox = {
					pswp: res?.pswp,
					rootElement: res?.rootElement,
					stripId,
				};
			}
		} catch (err) {
			console.error("Failed to open image viewer.", err);
		}
	};

	onTrashItem = async (attachmentId: string, targetIndex?: number) => {
		warning("This item will be deleted from the strip. Are you sure you want to delete this item?", {
			title: "Delete item",
		}).then(response => {
			if (response !== true) return;

			const view = this.editorView;
			if (!view) throw new Error("No editorView found");
			view.state.doc.forEach((block, blockOffset) => {
				if (block.type.name === this.name && this.lightbox?.stripId === block.attrs.blockId) {
					// Technically, it's possible to have the same attachment multiple times in one strip, but we don't want
					// to have to keep track of all the document changes inside the lightbox. So we simply specify what
					// attachment to delete from what strip, at its original index, and delete the nearest matching one. If
					// the attachments have been reordered while the lightbox was open, this may lead to deleting a different
					// one, but since the user won't have seen the change, it's arguably more correct anyway.
					let items: { pos: number; index: number }[] = [];
					block.forEach((item, itemOffset, itemIndex) => {
						if (item.firstChild?.attrs.attachmentId === attachmentId) {
							items.push({
								pos: blockOffset + itemOffset + 1,
								index: itemIndex,
							});
						}
					});
					if (items.length === 1 || targetIndex === undefined) {
						this.deleteItem(items[0].pos);
					} else if (items.length > 1) {
						let posToDelete = items.sort((a, b) => Math.abs(a.index - targetIndex) - Math.abs(b.index - targetIndex))[0]
							.pos;
						this.deleteItem(posToDelete);
					}
					this.lightbox?.pswp.close();
				}
			});
		});
	};

	onLightboxDestroyed = () => {
		if (this.lightbox) {
			document.body.removeChild(this.lightbox.rootElement);
			delete this.lightbox;
		}
	};

	setAttachmentDimensions(attachmentId: string, dimensions: FellowAttachment["dimensions"]) {
		const existing = this.dimensionsInStrip.get(attachmentId);
		if (!existing || !isEqual(existing, dimensions)) {
			this.dimensionsInStrip.set(attachmentId, dimensions);
			this.dimensionsChanged = true;
			this.triggerResize();
		}
	}

	private _triggerResize = () => {
		// make sure to wrap this inside of a setTimeout or something else that doesn't call this inline
		// to avoid nested view.dispatch.
		const { editorView } = this;
		if (!editorView) return;
		editorView.dispatch(editorView.state.tr.setMeta(ResizePluginKey, true));
	};

	triggerResize = debounce(this._triggerResize, 100, { trailing: true, leading: false });

	resizeOnStripWidthChange: ResizeObserverCallback = (entries: ResizeObserverEntry[]) => {
		for (let entry of entries) {
			if (entry.contentRect) {
				// When the editor view is not present in the DOM tree, skip the update.
				if (!document.contains(entry.target)) return;
				// When the editor has no size, it's hidden, so don't do anything to it.
				if (!entry.contentRect.height && !entry.contentRect.width) return;
				// since we have the contentRect here which doesn't include the padding of the .ProseMirror,
				// we're subtracting only the width of .blockActionContainer
				const blockActionContainer = entry.target.querySelector("[data-block-actions-container]");
				let blockActionContainerWidth = 20; // its default value is 1.25rem but will change base on screen size
				if (blockActionContainer instanceof Element) {
					const { width } = blockActionContainer.getBoundingClientRect();
					blockActionContainerWidth = width;
				}
				const stripWidth = Math.floor(entry.contentRect.width - blockActionContainerWidth);
				// do nothing if the stripWidth hasn't changed
				if (this.stripWidth && stripWidth === this.stripWidth) return;
				this.stripWidth = stripWidth;
				this.triggerResize();
			}
		}
	};

	initResizeObserver = () => {
		this.resizeObserver = new ResizeObserver(throttle(this.resizeOnStripWidthChange, 100, { trailing: true }));
		const { editorView } = this;
		if (!editorView) return;
		const editorDom = editorView.dom;
		if (editorDom instanceof Element) {
			this.resizeObserver.observe(editorDom);
		}
	};

	getStripWidth = () => {
		let editorDom: HTMLElement | undefined;
		const { editorView } = this;
		if (!editorView) {
			// On first mount, editor view hasn't being rendered. Use editorContainer as an acceptable approximation
			editorDom = this.editorContainer;
		} else {
			editorDom = editorView.dom;
		}
		if (editorDom instanceof Element) {
			const { width } = editorDom.getBoundingClientRect();
			const { paddingLeft, paddingRight } = getComputedStyle(editorDom);
			let blockActionContainerWidth = 20;
			const blockActionContainer = editorDom.querySelector("[data-block-actions-container]");
			if (blockActionContainer instanceof Element) {
				const { width } = blockActionContainer.getBoundingClientRect();
				blockActionContainerWidth = width;
			}
			// subtracting padding of .ProseMirror and width of .blockActionContainer;
			this.stripWidth = Math.floor(
				width - parseFloat(paddingLeft) - parseFloat(paddingRight) - blockActionContainerWidth,
			);
			return this.stripWidth;
		}
		return;
	};

	destroyResizeObserver = () => {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	};

	onPixelRatioChange = () => {
		this.devicePixelRatio = window.devicePixelRatio;
		// the query has changed when the ratio is changed so we need to assign a new query
		this.matchMediaQuery = window.matchMedia(`(resolution: ${this.devicePixelRatio}dppx)`);
		this.matchMediaQuery.addEventListener("change", this.onPixelRatioChange, { once: true });
		this.triggerResize();
	};

	private _uploadSingleFile = async (blockId: string, sessionId: string, file: File, replace?: boolean) => {
		const view = this.editorView;
		if (!view) return null;
		const tempId = uuid.v4();
		const nodeUploadingFiles: Map<string, File> = this.uploadingFiles.get(blockId) || new Map();
		nodeUploadingFiles.set(tempId, file);

		// link the uploading file with its position
		this.uploadingFilesPos.set(tempId, sessionId);

		// collect the dimension of the uploading file
		const fileDimensions = this.uploadingFilesDimensions.get(blockId) || new Map();
		const imgDimension = await this.getFileDimension(file);
		fileDimensions.set(file, { width: imgDimension.width, height: imgDimension.height });
		this.uploadingFilesDimensions.set(blockId, fileDimensions);
		view.dispatch(view.state.tr.setMeta(ResizePluginKey, true));

		this.uploadingFiles.set(blockId, nodeUploadingFiles);
		let attachmentId: undefined | string;
		try {
			const requestRes = await commitMutationPromise<imageStripAddMutation>(environment, {
				mutation: AddMutation,
				variables: {
					input: {
						contentId: this.options.contentId,
						filename: file.name,
						contentType: file.type || null,
						contentLength: file.size || null,
					},
				},
			});
			attachmentId = requestRes.requestAddAttachment.attachment?.id;
			if (!requestRes.requestAddAttachment.url || !attachmentId) {
				const graphqlErrorMsg =
					(requestRes.requestAddAttachment?.errors && requestRes.requestAddAttachment.errors[0]?.message) || "";
				if (graphqlErrorMsg) {
					throw new Error(graphqlErrorMsg);
				} else {
					throw new Error("failed to request upload url");
				}
			}
			const formData = new FormData();
			forEach(requestRes.requestAddAttachment.fields, (value, key) => formData.set(key, value));
			formData.append("file", file, file.name);
			const fetchRes = await fetch(requestRes.requestAddAttachment.url, { method: "POST", body: formData });
			if (!fetchRes.ok) {
				throw new Error(fetchRes.statusText);
			}
			const confirmationResponse = await commitMutationPromise<imageStripConfirmMutation>(environment, {
				mutation: ConfirmMutation,
				variables: {
					input: {
						attachmentId,
					},
				},
			});
			if (confirmationResponse) {
				let attachmentNode = confirmationResponse.confirmAddAttachment?.edge?.node;
				if (attachmentNode && attachmentNode.dimensions) {
					this.setAttachmentDimensions(attachmentNode.id, attachmentNode.dimensions);
				}
			}
			return tempId;
		} catch (error) {
			console.error(error);
			if (error instanceof Error) {
				toasty.error(`Failed to upload file: ${file.name} — ${error.message}.`);
			}
			return;
		} finally {
			nodeUploadingFiles.delete(tempId);
			this.uploadingFilesPos.delete(tempId);
			this.uploadingFilesDimensions.get(blockId)?.delete(file);
			// this must happen before the next two lines
			// so there won't be a time where the same image is both inserted and uploading
			if (attachmentId) {
				const pos = this.uploadingPos.get(sessionId);
				if (pos) {
					this.uploadedFiles.set(attachmentId, file);
					if (!replace) {
						addNewItemToStrip(pos, attachmentId)(view.state, view.dispatch);
					} else {
						replaceItemInStrip(pos, attachmentId)(view.state, view.dispatch);
					}
				}
			}
		}
	};

	cleanEmptyStrips = () => {
		const view = this.editorView;
		if (!view) return;
		const tr = view.state.tr.setMeta("clean-after-upload", true);
		view.state.doc.descendants((node, pos) => {
			if (node.type.name === "image_strip") {
				if (node.childCount === 0 && !this.uploadingFiles.get(node.attrs.blockId)?.size) {
					tr.delete(pos, pos + 2);
				}
			}
			return false;
		});
		if (tr.docChanged) {
			view.dispatch(tr);
		}
	};

	linkAttachment = (attachmentId: string, pos: number) => {
		const view = this.editorView;
		if (!view) return;
		addNewItemToStrip(pos, attachmentId)(view.state, view.dispatch);
		this.triggerResize();
		this.cleanEmptyStrips();
	};

	replaceFile = (file: File, pos: number) => {
		const view = this.editorView;
		if (!view) return;
		const blockId = view.state.doc.resolve(pos).node(1)?.attrs.blockId as string | undefined;
		if (!blockId) return;
		const sessionId = uuid4();
		this.uploadingPos.set(sessionId, pos);
		this._uploadSingleFile(blockId, sessionId, file, true).finally(() => {
			view.dispatch(view.state.tr.setMeta("force-redraw-decos", true));
			// Remove possible empty image strip
			this.cleanEmptyStrips();
			this.uploadingPos.delete(sessionId);
		});
		view.dispatch(view.state.tr.setMeta("force-redraw-decos", true));
	};

	uploadFiles = (files: File[], pos: number) => {
		const view = this.editorView;
		if (!view) return;
		const stripNode = view.state.doc.resolve(pos).node(1);
		if (!stripNode) return;
		const blockId = view.state.doc.resolve(pos).node(1)?.attrs.blockId as string | undefined;
		if (!blockId) return;

		const sessionId = uuid4();
		this.uploadingPos.set(sessionId, pos);

		const childCount = stripNode.childCount;
		const uploadingFilesCount = this.uploadingFiles.get(blockId)?.size ?? 0;
		const itemsInStripCount = childCount + uploadingFilesCount;
		const uploadingFiles = files.slice(0, IMAGE_STRIP_MAX_CAPACITY - itemsInStripCount);
		const remainingFiles = files.slice(IMAGE_STRIP_MAX_CAPACITY - itemsInStripCount);

		const promises = uploadingFiles.map(f => this._uploadSingleFile(blockId, sessionId, f, false));
		Promise.all(promises)
			.then(results => {
				console.log(`uploaded ${compact(results).length} files`);
			})
			.finally(() => {
				// this is to clean up uploading indicators
				view.dispatch(view.state.tr.setMeta("force-redraw-decos", true));
				// Remove possible empty image strip
				this.cleanEmptyStrips();
				const stripPos = this.uploadingPos.get(sessionId);
				this.uploadingPos.delete(sessionId);
				if (remainingFiles.length && stripPos) {
					const endOfCurrentStripPos = view.state.doc.resolve(stripPos).end(1);
					const insertedNewImageStrip = insertImageStrip(stripPos + 1)(view.state, view.dispatch);
					if (insertedNewImageStrip) {
						this.uploadFiles(remainingFiles, endOfCurrentStripPos + 2);
					}
				}
			});
		// this is to render uploading indicators
		view.dispatch(view.state.tr.setMeta("force-redraw-decos", true));
	};

	/**
	 * Delete an image_strip_item from an image_strip, and if it was the last item in the strip, delete the strip as well.
	 * @param itemPos - The position of the image_strip_item node to be deleted (the position before its opening token)
	 */
	deleteItem = (itemPos: number) => {
		if (!this.editorView) throw new Error("ImageStrip extension has no editorView set");
		const { state, dispatch } = this.editorView,
			{ doc } = state;

		const $itemPos = doc.resolve(itemPos),
			stripNode = $itemPos.parent,
			itemNode = $itemPos.nodeAfter;

		if (!itemNode) throw new RangeError("No node found at position");
		if (itemNode.type.name !== "image_strip_item")
			throw new TypeError(`Found invalid node type "${itemNode.type.name}" at itemPos - expected "image_strip_item"`);

		const { tr } = state;
		if (stripNode.childCount === 1) {
			tr.delete($itemPos.before(), $itemPos.after());
		} else {
			tr.delete(itemPos, itemPos + itemNode.nodeSize);
			tr.setMeta(ResizePluginKey, { force: true });
		}

		dispatch(tr);
	};

	renderCaptionPlaceholder = () => {
		const node = document.createElement("span");
		node.classList.add(styles.captionPlaceholder);
		node.innerText = "Add a caption";
		return node;
	};

	renderUploadingFile = (file: File, strip: ProseNode) => (_view: EditorView, _getPos: () => number | undefined) => {
		const dom = document.createElement("div");
		dom.classList.add(styles.imageStripItem);
		let style = "";
		let imgStyle = "";
		const { stripHeight, getItemWidth } = this.getStripHeight(strip);
		const dimension = this.uploadingFilesDimensions.get(strip.attrs["blockId"])?.get(file);
		const ratio = !!dimension && !!dimension.width && !!dimension.height ? dimension.width / dimension.height : 1;
		const itemWidth = getItemWidth(ratio, dimension);
		const objectFit = objectFitMode(ratio, dimension, itemWidth, stripHeight, this.devicePixelRatio);
		style = `height: ${stripHeight}px; width: ${itemWidth}px; position: relative;`;
		imgStyle = `object-fit: ${objectFit}`;
		dom.innerHTML = `
			<div class="${styles.thumbnailWrapper}" style="${style}">
			${
				file.type.startsWith("image")
					? `<img
						src="${URL.createObjectURL(file)}" alt="preview" class="${cx(
							styles.imageStripInlinePreviewContent,
							styles.uploadingImage,
						)}" style="${imgStyle}">`
					: ""
			}
				<div class="${styles.imageStripItemLoader}"><div class="ui text loader active"></div></div>
			</div>
			<div class="${styles.imageStripItemCaption}"></div>
		`;
		return dom;
	};

	get plugins() {
		const extension = this;
		const imageStripPlugin = new Plugin<{ dragOverPos: number | null }>({
			filterTransaction: tr => {
				this.uploadingPos = new Map(Array.from(this.uploadingPos.entries()).map(([k, v]) => [k, tr.mapping.map(v)]));
				return true;
			},
			props: {
				transformPasted: (slice: Slice) => {
					const posToDelete: [number, number?, number?][] = [];
					slice.content.forEach((node, index) => {
						if (node.type.name === "image_strip_item" && !node.firstChild?.attrs.attachmentId) {
							posToDelete.push([index, undefined, undefined]);
						}
						node.descendants((node, pos) => {
							if (node.type.name === "image_strip_item" && !node.firstChild?.attrs.attachmentId) {
								posToDelete.push([index, pos, pos + node.nodeSize]);
							}
						});
					});
					if (posToDelete.length) {
						// @ts-ignore
						const newNodes: (ProseNode | null)[] = [...slice.content.content];
						const nodeTr: { [key: number]: Transform } = {};
						for (let [index, start] of posToDelete) {
							if (typeof start === "undefined") {
								newNodes[index] = null;
							}
							const node = newNodes[index];
							if (!nodeTr[index] && node) nodeTr[index] = new Transform(node);
						}
						for (let [index, start, end] of posToDelete) {
							const tr = nodeTr[index];
							if (tr && typeof start !== "undefined" && typeof end !== "undefined") {
								tr.delete(tr.mapping.map(start), tr.mapping.map(end));
							}
						}
						for (const [index, tr] of Object.entries(nodeTr)) {
							if (tr.doc.type.name === "image_strip" && !tr.doc.content.size) {
								if (tr.docs[0].textContent) {
									newNodes[parseInt(index, 10)] = tr.doc.type.schema.text(tr.docs[0].textContent);
								} else {
									newNodes[parseInt(index, 10)] = null;
								}
								continue;
							}
							newNodes[parseInt(index, 10)] = tr.doc;
						}

						const newSlice = new Slice(Fragment.fromArray(compact(newNodes)), 0, 0);
						return newSlice.size > 0 ? newSlice : new Slice(Fragment.fromArray([]), 0, 0);
					} else {
						return slice;
					}
				},
				decorations: (state: EditorState) => {
					const editable = extension.editorView?.editable;
					if (extension.options.disable || (!editable && extension.options.disableWhenNotEditable)) return;
					const decos: Decoration[] = [];
					state.doc.descendants(node => {
						if (node.type.name === extension.name) {
							let uploadingFileIndex = 10;
							const nodeUploadingFiles = extension.uploadingFiles.get(node.attrs.blockId);
							if (nodeUploadingFiles) {
								for (const [key, uploadingFile] of nodeUploadingFiles.entries()) {
									// retrieve the position of the uploading file to apply decoration
									const uploadingPosSessionId = extension.uploadingFilesPos.get(key);
									const uploadingPos = uploadingPosSessionId
										? extension.uploadingPos.get(uploadingPosSessionId)
										: undefined;
									if (uploadingPos) {
										// This relies on a transaction being dispatched at the right time.
										const deco = Decoration.widget(uploadingPos, extension.renderUploadingFile(uploadingFile, node), {
											side: uploadingFileIndex,
										});
										uploadingFileIndex += 1;
										decos.push(deco);
									}
								}
							}
						}
						return false;
					});
					return DecorationSet.create(state.doc, decos);
				},
				handleTextInput: (view: EditorView, _from: number, _to: number, _text: string) => {
					const {
						state: {
							selection: { from, to },
						},
					} = view;
					if (!isImageStripInRange(view.state.doc, from, to)) return false;
					if (!isRangeInSingleCaption(view.state.doc, from, to)) {
						return true;
					}
					return false;
				},
				handlePaste(view: EditorView, event: Event, _slice: Slice) {
					if (!(event instanceof ClipboardEvent)) return false;
					if (!event.clipboardData || !event.clipboardData.files || !event.clipboardData.files.length) return false;

					const { empty, $from } = view.state.selection;
					if (!empty) return false;
					let stripPos = null;
					if ($from.node(1).type.name === extension.name) {
						stripPos = $from.start(1);
					} else {
						insertImageStrip($from.end(1) + 1)(view.state, view.dispatch);
						stripPos = $from.end(1) + 2;
					}
					if (stripPos) {
						extension.uploadFiles(Array.from(event.clipboardData.files), stripPos);
						return true;
					}
					return false;
				},
				handleClickOn(_view, _pos, node, _nodePos, event, direct) {
					// Prose's default behaviour is to select a leaf node when you click it – by returning true here, we prevent
					// that, since we're gonna open the lightbox instead. This is especially important on iOS, because it shows
					// the selection highlight overtop of the lightbox in some circumstances.
					const { state, dispatch } = _view;

					if (direct && node.type.name === "image_strip_item_file") {
						// They clicked on the actual file, so prevent prose from doing anything.
						return true;
					} else if (direct && node.type.name === "image_strip") {
						// They clicked on the strip part, so dispatch a transaction to place the cursor near to the click location
						const {
							selection: { from, to },
							doc,
							tr,
						} = state;
						if (isToCollapse(event.target)) return true;
						if (isImageStripInRange(doc, from, to)) {
							// Selection already includes the image_strip
							return false;
						}
						// Look backwards first to find first node in the selection (bias=-1)
						let newSelection = TextSelection.near(state.doc.resolve(_pos), -1);
						if (!isImageStripInRange(doc, newSelection.from, newSelection.to)) {
							// If the nearest text node isn't within the image strip (they clicked near the beginning of the el,
							// then try looking forwards
							newSelection = TextSelection.near(state.doc.resolve(_pos));
						}
						// if we still couldnt find a selection in range, return;
						if (!isImageStripInRange(doc, newSelection.from, newSelection.to)) return false;
						// dispatch a transaction setting new selection.
						tr.setSelection(newSelection);
						dispatch(tr);
						return true;
					}
					return false;
				},
			},
		});
		const imageStripResizePlugin = new Plugin<ResizePluginSpec>({
			key: ResizePluginKey,
			appendTransaction: (trs, oldState, newState) => {
				let isMultiselectDrop = false;
				const remoteOrHistoryChanges = trs.filter(tr => {
					if (!tr.docChanged) return false;
					if (tr.getMeta("multiselect-adjusted")) {
						isMultiselectDrop = true;
						return false;
					}
					return tr.getMeta("remoteChange") || tr.getMeta("history$") || tr.getMeta("paste");
				});
				if (isMultiselectDrop || (remoteOrHistoryChanges.length > 0 && isStripsNeedResize(oldState, newState))) {
					const { tr } = newState;
					return tr.setMeta(ResizePluginKey, true);
				}
				return null;
			},
			state: {
				init(_config, { doc }) {
					return {
						decorationSet: DecorationSet.create(doc, extension.resize(extension.dimensionsInStrip, doc)),
					};
				},
				// TODO: instead of resize all the strips, should only resize the strips that are affected.
				// Can possibly have a "dirtyStrips" in the plugin meta to indicate which strips should be resize
				apply(tr, value) {
					const resizeMeta: ResizePluginMeta = tr.getMeta(ResizePluginKey);
					if (resizeMeta !== undefined || extension.dimensionsChanged) {
						const newDecorationSet = DecorationSet.create(
							tr.doc,
							extension.resize(extension.dimensionsInStrip, tr.doc),
						);
						extension.dimensionsChanged = false;
						return {
							...value,
							decorationSet: newDecorationSet,
						};
					}
					return {
						...value,
						decorationSet: value.decorationSet.map(tr.mapping, tr.doc),
					};
				},
			},
			props: {
				decorations: state => {
					return ResizePluginKey.getState(state).decorationSet;
				},
			},
		});
		const imageStripItemActivePlugin = new Plugin({
			key: ItemActivePluginKey,
			props: {
				decorations: state => {
					const { selection, doc } = state;
					const editable = extension.editorView?.editable;
					if (extension.options.disable || (!editable && extension.options.disableWhenNotEditable)) return;
					if (isSelectingItemFile(selection)) {
						const decos: Decoration[] = [];
						const {
							$from: { pos, nodeAfter, parent },
						} = selection;
						decos.push(
							Decoration.node(pos, pos + (nodeAfter as ProseNode).nodeSize, {
								class: styles.itemActive,
							}),
						);
						if (parent) {
							parent.descendants(node => {
								if (
									node.type.name === "image_strip_item_caption" &&
									node.textBetween(0, node.content.size, "\n").length === 0
								) {
									const widgetPos = pos + (nodeAfter as ProseNode).nodeSize + 1;
									decos.push(Decoration.widget(widgetPos, extension.renderCaptionPlaceholder));
								}
							});
						}
						return DecorationSet.create(doc, decos);
					}
					return undefined;
				},
			},
		});
		return [imageStripPlugin, imageStripResizePlugin, imageStripItemActivePlugin];
	}

	menuEntry(view: EditorView) {
		return {
			title: "Attachments strip",
			subtitle: "Add files to the note.",
			tooltip: "Upload files",
			icon: faPhotoVideo,
			active: (state: EditorState) => nodeActive(this.name)(state),
			section: "types" as const,
			group: "insert",
			hideOnMediumScreen: true,
			run: () => {
				return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
					const input = this.inputElement;
					const onChange = (e: Event) => {
						e.preventDefault();
						e.stopPropagation();
						if (!input.files) return;
						if (!view) return;
						const stripPos = view.state.doc.resolve(view.state.selection.from).end(1) + 1;
						const imageStripExt = view.extensions.get("image_strip") as ImageStrip | undefined;
						if (!imageStripExt) return;
						if (imageStripExt) {
							insertImageStrip(stripPos)(state, dispatch);
							imageStripExt.uploadFiles(Array.from(input.files), stripPos + 1);
						}
					};
					input.addEventListener(
						"change",
						e => {
							onChange(e);
						},
						{ once: true },
					);
					input.click();
					return true;
				};
			},
		};
	}

	keys() {
		return {
			Backspace: (state: EditorState, _dispatch?: (tr: Transaction) => void) => {
				const {
					selection: { from, to, $from, empty },
					doc,
				} = state;
				if (!isImageStripInRange(doc, from, to)) return false;
				if (!isRangeInSingleCaption(doc, from, to)) return true;
				if ($from.parentOffset === 0 && empty) {
					// backspace at start of caption
					return true;
				}
				return false;
			},
			Delete: (state: EditorState) => {
				const {
					selection: { from, to },
					doc,
				} = state;
				if (!isImageStripInRange(doc, from, to)) return false;
				if (!isRangeInSingleCaption(doc, from, to)) return true;
				return false;
			},
			Enter: (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				const {
					selection: { from, to, $from, empty },
					doc,
				} = state;
				if (!isImageStripInRange(doc, from, to)) return false;
				if (!empty) {
					return true;
				}
				const newNode = state.schema.nodes.unstyled.createAndFill({ blockId: uuid4() });
				if (!newNode) return true;
				if (dispatch) {
					const tr = state.tr;
					tr.replaceRangeWith($from.end(1), $from.end(1), newNode);
					tr.setSelection(TextSelection.create(tr.doc, $from.end(1) + 2));
					dispatch(tr);
				}
				return true;
			},
		};
	}

	resize = (map: typeof this.dimensionsInStrip, doc: ProseNode): Decoration[] => {
		const decos: Decoration[] = [];
		let stripHeight: number;
		let getItemWidth: (ratio: number, dimension: { width?: number; height?: number } | undefined) => number;

		doc.descendants((node, pos) => {
			if (node.type.name === "image_strip") {
				if (node.childCount > IMAGE_STRIP_MAX_CAPACITY) return false;
				const { stripHeight: height, getItemWidth: getWidth } = this.getStripHeight(node);
				stripHeight = height;
				getItemWidth = getWidth;
				return true;
			}
			if (node.type.name === "image_strip_item") {
				if (node.firstChild) {
					const attachmentId = node.firstChild.attrs["attachmentId"];
					const dimensions = map.get(attachmentId);
					const ratio =
						!!dimensions && !!dimensions.width && !!dimensions.height ? dimensions.width / dimensions.height : 1;

					const itemWidth = getItemWidth(ratio, dimensions);
					const style = `height: ${stripHeight}px; width: ${itemWidth}px;`;
					const objectFitClass = objectFitMode(ratio, dimensions, itemWidth, stripHeight, this.devicePixelRatio);
					decos.push(
						// this decoration is applied to the "image_strip_item_file" node,
						// which should be the first node under "image_strip_item"
						Decoration.node(pos + 1, pos + 1 + node.firstChild.nodeSize, {
							style: style,
							class: cx({
								[styles.fitContain]: objectFitClass === "contain",
								[styles.fitNone]: objectFitClass === "none",
							}),
						}),
					);
				}
				return false;
			}
			return false;
		});
		return decos;
	};

	/**
	 * This function calculates the strip's height according to the dimensions of items inside it,
	 * and also returns a function to calculate each item's width according to their ratio/dimensions.
	 * @param strip the strip going to calculate with
	 * @returns stripHeight: The height the strip should has
	 *	getItemWidth: A function that generates a closure with the context that are needed when calculating an item's width
	 */
	getStripHeight = (
		strip: ProseNode,
	): {
		stripHeight: number;
		getItemWidth: (ratio: number, dimension: { width?: number; height?: number } | undefined) => number;
	} => {
		const stripWidth = this.stripWidth || this.getStripWidth();
		if (typeof stripWidth === "undefined") {
			console.error("Failed to get the width of the strip.");
			return {
				stripHeight: FALLBACK_HEIGHT,
				getItemWidth: () => FALLBACK_HEIGHT,
			};
		}
		const DEFAULT_MIN_HEIGHT_PCT = DEFAULT_MIN_HEIGHT / stripWidth;
		const DEFAULT_HEIGHT = DEFAULT_HEIGHT_PCT * stripWidth;

		// collect the dimensions of the items that need to take into consideration
		// including the items already uploaded and are uploading in the strip
		const dimensions: ({ width: number | undefined; height: number | undefined } | undefined)[] = [];
		strip.content.forEach(n => {
			// get attachmentId from the 'image_strip_item_file' node, which should be the first child of each node
			const attachmentId: string = n.firstChild?.attrs["attachmentId"];
			const dimension = this.dimensionsInStrip.get(attachmentId);
			dimensions.push(dimension);
		});
		// get the uploading files in the strip
		const uploadingFilesInStrip = this.uploadingFilesDimensions.get(strip.attrs["blockId"]);
		if (uploadingFilesInStrip) {
			uploadingFilesInStrip.forEach(d => {
				dimensions.push(d);
			});
		}

		// adjust dimension base on devicePixelRatio so that images won't blur on Retina or HiDPI displays
		// see https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio
		const adjustedDimensions = dimensions.map(d => {
			if (!d || !d.width || !d.height) return d;
			return {
				width: d.width / this.devicePixelRatio,
				height: d.height / this.devicePixelRatio,
			};
		});

		// if there's only one item to display there would be much less factors to consider
		if (adjustedDimensions.length === 1) {
			if (!adjustedDimensions[0] || !adjustedDimensions[0].width || !adjustedDimensions[0].height) {
				return {
					stripHeight: DEFAULT_HEIGHT,
					getItemWidth: _ratio => DEFAULT_HEIGHT,
				};
			}
			let { height, width } = adjustedDimensions[0];
			if (width > stripWidth) {
				height *= stripWidth / width;
			}
			const stripHeightPct = Math.max(Math.min(height, DEFAULT_MAX_HEIGHT) / stripWidth, DEFAULT_MIN_HEIGHT_PCT);
			return {
				stripHeight: stripHeightPct * stripWidth,
				getItemWidth: ratio => {
					return (
						Math.min(
							ratio < 0.5 ? Math.max(DEFAULT_MIN_WIDTH_PCT, stripHeightPct * ratio) : stripHeightPct * ratio,
							1,
						) * stripWidth
					);
				},
			};
		}

		let ratioSum = 0; // summing up the ratios
		let minWidthCount = 0; // how many items with low ratio (r < 0.5) should get a min width
		let maxHeightPct = 0; // record the max height (in percentage to the strip's width) among the items in a strip
		let widthSum = 0; // summing up the width of items (for items with r > 0.5 take their natural width and for r < 0.5 take the resized width)
		// is used to calculate the width when stripHeightPct is set to minHeightPct
		let defaultHeightCount = 0; // how many items get the default height
		let globalWidthRestrictedScale = 1;
		adjustedDimensions.forEach(d => {
			if (!d || !d.width || !d.height) {
				widthSum += DEFAULT_HEIGHT;
				return;
			}
			const ratio = d.width / d.height;
			if (ratio < 0.5) {
				widthSum += DEFAULT_MIN_WIDTH_PCT * stripWidth;
			} else if (ratio > 2) {
				widthSum += d.width;
			} else {
				widthSum += d.width;
			}
		});
		if (widthSum > stripWidth) {
			globalWidthRestrictedScale = stripWidth / widthSum;
		}
		adjustedDimensions.forEach(d => {
			if (!d || !d.width || !d.height) {
				// if the dimension don't exist, meaning the file is a non-image file which has no dimension
				// or it's somehow missing, we can use a set of default dimension which gives a square ratio
				ratioSum += 1;
				maxHeightPct = Math.max(DEFAULT_HEIGHT_PCT, maxHeightPct);
				defaultHeightCount += 1;
				return;
			}
			const ratio = d.width / d.height;
			maxHeightPct = Math.max((d.height * globalWidthRestrictedScale) / stripWidth, maxHeightPct);
			if (ratio < 0.5) {
				minWidthCount += 1;
			} else if (ratio > 2) {
				ratioSum += 2;
			} else {
				ratioSum += ratio;
			}
		});

		const remainWidthSum =
			widthSum - minWidthCount * DEFAULT_MIN_WIDTH_PCT * stripWidth - defaultHeightCount * DEFAULT_HEIGHT;
		// remainWidthSum(px) is used to calculate the width PERCENTAGE an item (which does not get a fixed width) should take
		// (in proportion of the width after subtracting all the possible fixed widths from its sibilings)
		const remainWidth =
			(1 - minWidthCount * DEFAULT_MIN_WIDTH_PCT - defaultHeightCount * DEFAULT_HEIGHT_PCT) * stripWidth;
		// remainWidth(px) is used to calculate the ACTUAL width an item should have
		const stripHeightPct = Math.max(
			Math.min(
				maxHeightPct,
				(1 - DEFAULT_MIN_WIDTH_PCT * minWidthCount) / ratioSum,
				// at most we can have a max height
				DEFAULT_MAX_HEIGHT / stripWidth,
			),
			// at least we have the min height
			DEFAULT_MIN_HEIGHT_PCT,
		);

		return {
			stripHeight: stripHeightPct * stripWidth,
			getItemWidth: (ratio, dimension) => {
				const adjustedDimension = {
					width: dimension?.width ? dimension?.width / this.devicePixelRatio : undefined,
					height: dimension?.height ? dimension?.height / this.devicePixelRatio : undefined,
				};
				if (stripHeightPct === maxHeightPct || stripHeightPct === DEFAULT_MIN_HEIGHT_PCT) {
					if (!adjustedDimension || !adjustedDimension.width || !adjustedDimension.height) {
						return DEFAULT_HEIGHT;
					}
					if (ratio < 0.5) return DEFAULT_MIN_WIDTH_PCT * stripWidth;
					const widthPct = adjustedDimension.width / remainWidthSum;
					return widthPct * remainWidth;
				}
				const widthPct =
					ratio < 0.5
						? Math.max(DEFAULT_MIN_WIDTH_PCT, stripHeightPct * ratio)
						: ratio > 2
							? 2 * stripHeightPct
							: stripHeightPct * ratio;
				return widthPct * stripWidth;
			},
		};
	};

	getFileDimension = (file: File) => {
		if (!file.type.startsWith("image")) {
			// if is not an image file return a set of square dimension with default height
			const stripWidth = this.stripWidth || this.getStripWidth();
			if (typeof stripWidth === "number") {
				return { width: DEFAULT_HEIGHT_PCT * stripWidth, height: DEFAULT_HEIGHT_PCT * stripWidth };
			}
			return { width: FALLBACK_HEIGHT, height: FALLBACK_HEIGHT };
		}
		return new Promise<{ width: number; height: number }>(resolve => {
			const img = new Image();
			img.onload = () => {
				URL.revokeObjectURL(img.src);
				resolve({ width: img.naturalWidth, height: img.naturalHeight });
			};
			img.src = URL.createObjectURL(file);
		});
	};

	getUploadImageMenu({
		view,
		getPos,
		text,
	}: {
		view: EditorView;
		getPos: () => number;
		text: string;
		callback?: () => void;
	}) {
		return {
			id: "uploadImage",
			icon: faPlus,
			right: text,
			action: (close: () => void) => {
				if (!view.editable) return false;
				const input = this.inputElement;
				const onChange = (e: Event) => {
					const imageStripExt = view.extensions.get("image_strip") as ImageStrip | undefined;
					if (!imageStripExt) return;
					e.preventDefault();
					e.stopPropagation();
					if (!input.files) return;
					if (!view) return;
					if (imageStripExt) {
						const { state, dispatch } = view;
						const stripPos = getPos();
						const { nodeAfter } = state.doc.resolve(stripPos);
						if (nodeAfter) {
							const { isFull } = isStripFull(nodeAfter, imageStripExt);
							if (!isFull) {
								imageStripExt.uploadFiles(Array.from(input.files), stripPos + nodeAfter.nodeSize - 1);
							} else {
								const newStripPos = stripPos + nodeAfter.nodeSize;
								insertImageStrip(newStripPos)(state, dispatch);
								imageStripExt.uploadFiles(Array.from(input.files), newStripPos + 1);
							}
						}
					}
				};
				input.addEventListener(
					"change",
					e => {
						onChange(e);
					},
					{ once: true },
				);
				input.click();
				close();
				return true;
			},
			disabled: !view.editable,
		};
	}
}

export class ImageStripItem extends NodeExtension {
	get name() {
		return "image_strip_item";
	}
	get node(): NodeExtension["node"] {
		return {
			content: "image_strip_item_file image_strip_item_caption",
			defining: true,
			attrs: {},
			parseDOM: [
				{
					tag: `[data-block-type="${this.name}"]`,
				},
			],
			toDOM: node => [
				"div",
				{
					class: styles.imageStripItem,
					"data-block-type": node.type.name,
				},
				0,
			],
		};
	}
}

const ImageStripItemInlinePreview: React.FC<ReactNodeViewProps> = ({ node, view, getPos }) => {
	const imageStripExt = view.extensions.get("image_strip") as ImageStrip;
	const editable = view.editable;
	const disabled = imageStripExt.options.disable || (!editable && imageStripExt.options.disableWhenNotEditable);
	const { hidePreview, requestAttachment } = imageStripExt.options;
	const onDelete = React.useCallback(() => {
		if (typeof getPos !== "function") return;
		const $thumbPos = view.state.doc.resolve(getPos());
		imageStripExt.deleteItem($thumbPos.before());
	}, [view, imageStripExt, getPos]);
	const inputRef = React.useRef<HTMLInputElement>(null);
	const onClickReplace = React.useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setSelection(Selection.atStart(tr.doc));
			dispatch(tr);
			inputRef.current?.click();
		},
		[view],
	);
	const onReplace = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			e.stopPropagation();
			e.preventDefault();
			if (typeof getPos !== "function") return;
			const file = e.target.files?.item(0);
			if (!file) return;
			imageStripExt.replaceFile(file, getPos() - 1);
			blur();
		},
		[getPos, imageStripExt],
	);
	const localFile = imageStripExt.uploadedFiles.get(node.attrs.attachmentId);

	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, fileType: string) => {
			if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
			if (typeof getPos !== "function") return;
			e.preventDefault();
			const {
				state: { tr, selection },
				dispatch,
			} = view;
			blur();
			if (!isSelectingItemFile(selection, node)) {
				tr.setSelection(NodeSelection.create(tr.doc, getPos()));
				dispatch(tr);
			}
			const { href } = e.currentTarget;
			// if is not image, do not open lightbox
			if (!fileType.startsWith("image")) {
				window.open(href, "_blank");
				return;
			}
			const lightboxPos = getPos();
			if (inVideocallMode) {
				// If this is an embedded view (e.g. in Google Meet), dont use lightbox.
				window.open(href, "_blank");
			} else {
				(async () => {
					try {
						await imageStripExt.openImageViewer(lightboxPos);
					} catch (exc) {
						if (window.SENTRY_DSN_PUBLIC) captureException(exc);
						if (isZoom) {
							zoomOpenUrl(href);
						} else {
							window.open(href, "_blank");
						}
					}
				})();
			}
		},
		[getPos, imageStripExt, view, node],
	);

	const dimensionCollector = React.useCallback(
		(attachmentId: string, dimensions: FellowAttachment["dimensions"]) => {
			imageStripExt.setAttachmentDimensions(attachmentId, dimensions);
		},
		[imageStripExt],
	);

	const onDragStart = React.useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			e.currentTarget.classList.add(styles.grabbing);
			blur();
			if (typeof getPos === "function") {
				const nodePos = getPos();
				const resolvedPos = view.state.doc.resolve(nodePos);
				const stripNode = resolvedPos.node(1);
				const startPos = nodePos - 1;
				const endPos = startPos + resolvedPos.parent.nodeSize;
				const slice = view.state.doc.slice(startPos, endPos);
				const dragSource = {
					view,
					slice: slice.toJSON(),
					startPos,
					endPos,
					itemIndex: resolvedPos.index(1),
					lastItem: stripNode.childCount === 1,
					stripId: stripNode.attrs["blockId"],
				};
				ImageStripItemDragSourceSingleton.set(dragSource);
				// We need these information to determine whether a position is valid to drop the item we just dragged,
				// and since it is not allowed to access the data in a dataTransfer during the "dragover" event, and plus
				// we need to allow dragging items between different note editor (which means prose plugin is not sufficient,
				// since it is defined by editor view), we use a global singleton here to store this piece of info and
				// consume it when validating the dropping position and when actually dropping the items.
				e.dataTransfer.clearData();
				// need to set this so that we can know which drag event handler to use
				e.dataTransfer.setData("fellow/image-strip-item", "");
				const {
					state: { tr },
				} = view;
				tr.setSelection(NodeSelection.create(tr.doc, nodePos)); // set the selection to current node
				view.dispatch(tr);
			}
		},
		[view, getPos],
	);

	const onDragEnd = React.useCallback((e: React.DragEvent<HTMLElement>) => {
		e.currentTarget.classList.remove(styles.grabbing);
		ImageStripItemDragSourceSingleton.clear();
	}, []);

	if (hidePreview) {
		return (
			<div className={styles.imageStripInlineHiddenPreview}>
				<MemoizedFontAwesomeIcon
					icon={faFileTimes}
					fixedWidth
					size="3x"
					className={styles.imageStripInlinePreviewContent}
				/>
			</div>
		);
	}

	return (
		<>
			<AttachmentPreview
				id={node.attrs.attachmentId}
				requestAttachment={requestAttachment}
				className={styles.imageStripInlinePreviewContent}
				innerClassName={styles.imageStripInlinePreviewContentInner}
				localFile={localFile}
				onClick={onClick}
				dimensionCollector={dimensionCollector}
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
			/>
			{!disabled && (
				<div className={styles.imageStripItemToolbar}>
					<Tooltip content="Replace" placement="bottom">
						<button type="button" className={styles.imageStripItemToolbarItem} onClick={onClickReplace} title="Replace">
							<ReplaceIcon />
							<input type="file" style={{ display: "none" }} onChange={onReplace} ref={inputRef} />
						</button>
					</Tooltip>
					<Tooltip content="Remove" placement="bottom">
						<button type="button" className={styles.imageStripItemToolbarItem} onClick={onDelete} title="Remove">
							<MemoizedFontAwesomeIcon icon={farTrashAlt} fixedWidth />
						</button>
					</Tooltip>
				</div>
			)}
		</>
	);
};

class ImageStripItemView extends ReactNodeView {
	getContainer() {
		const imageStripExt = this.view.extensions?.get("image_strip") as ImageStrip;
		const dom = document.createElement("div");
		dom.classList.add(styles.thumbnailWrapper);
		if (imageStripExt?.options.hidePreview) {
			dom.classList.add("previewHidden");
		}
		dom.contentEditable = "false";
		return dom;
	}
	getComponent() {
		return ImageStripItemInlinePreview;
	}
	selectNode = () => {
		return false;
	};
	ignoreMutation() {
		return true;
	}
	stopEvent(e: Event) {
		// stop prose from handling the drag events bubble up from this nodeview
		return /drag/.test(e.type);
	}
}

export class ImageStripItemFile extends NodeExtension {
	get name() {
		return "image_strip_item_file";
	}
	view = (node: ProseNode, view: EditorView, getPos: getPosHandler, decorations: readonly Decoration[]) => {
		return new ImageStripItemView(node, view, getPos, decorations);
	};
	get node(): NodeExtension["node"] {
		return {
			draggable: false,
			atom: true,
			attrs: {
				attachmentId: { default: null },
			},
			toDOM: node => [
				"div",
				{
					"data-block-type": node.type.name,
					attachmentId: node.attrs.attachmentId,
				},
			],
			parseDOM: [
				{
					tag: `[data-block-type="${this.name}"]`,
					getAttrs: dom => {
						if (!(dom instanceof Element)) return null;
						const attachmentId = dom.getAttribute("attachmentId");
						if (!attachmentId) return false;
						return { attachmentId };
					},
				},
			],
		};
	}
}

export class ImageStripItemCaption extends NodeExtension {
	get name() {
		return "image_strip_item_caption";
	}
	view = (node: ProseNode, view: EditorView, getPos: getPosHandler, _decorations: readonly Decoration[]) => {
		const dom = document.createElement("div");
		const contentDOM = document.createElement("div");
		contentDOM.setAttribute("pmcontent", "");
		dom.classList.add(styles.imageStripItemCaption);
		dom.appendChild(contentDOM);
		const mouseDownHandler = this.onMouseDown(node, view, getPos);
		dom.addEventListener("mousedown", mouseDownHandler);
		return {
			dom,
			contentDOM,
			destroy: () => {
				dom.removeEventListener("mousedown", mouseDownHandler);
			},
		};
	};

	// When trying to start adding a caption by clicking the "Add a caption", the selection will be first
	// removed from the "image_strip_item_file" node, at that point since the selection is not inside the
	// strip and the caption is empty, the caption node will be hide before we get a chance to type anything
	// in it, so here we need to set the selection into the caption node before it is set to somewhere else
	onMouseDown = (node: ProseNode, view: EditorView, getPos: getPosHandler) => (e: MouseEvent) => {
		const {
			state: {
				tr,
				selection: { from, to, $from },
				doc,
			},
			dispatch,
		} = view;
		// if the selectin is already inside this caption node or if the caption isn't empty,
		// we don't need to worry about the flicker and can leave it to other handlers if there's any
		if ((isRangeInSingleCaption(doc, from, to) && node === $from.node(3)) || node.textContent.length !== 0) return;

		// otherwise we need to prevent prose from setting the selection to elsewhere,
		// and manually set it inside the caption
		e.preventDefault();
		e.stopPropagation();
		if (typeof getPos === "function") {
			tr.setSelection(TextSelection.create(tr.doc, getPos() + 1));
			dispatch(tr);
			view.focus();
			return;
		}
	};

	get node(): NodeExtension["node"] {
		return {
			draggable: false,
			content: "(text | inline)*",
			toDOM: () => ["div", { class: styles.imageStripItemCaption }, 0],
		};
	}
	keys() {
		return {
			"Mod-a": (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				const { selection } = state;
				if (selection.$from.sameParent(selection.$to) && selection.$from.node().type.name === this.name) {
					const tr = state.tr;
					let depth = selection.$from.depth;
					while (depth >= 0 && selection.$from.node(depth).type.name !== this.name) {
						depth -= 1;
					}
					const start = selection.$from.start(depth);
					const end = selection.$from.end(depth);
					if (start === selection.from && end === selection.to) return false;
					tr.setSelection(TextSelection.create(tr.doc, selection.$from.start(depth), selection.$from.end(depth)));
					dispatch?.(tr);
					return true;
				}
				return false;
			},
		};
	}
	get plugins() {
		return [
			new Plugin({
				props: {
					handlePaste(view: EditorView, event: Event, slice: Slice) {
						if (!(event instanceof ClipboardEvent) || !event.clipboardData) return false;
						const { doc, selection, tr } = view.state;

						// Only handle paste while in image strip item caption
						const parentNodes = [selection.$from.parent.type.name, selection.$to.parent.type.name];
						if (!parentNodes.includes("image_strip_item_caption")) return false;

						// Check content being pasted is not just text
						if (slice.content.childCount === 1) {
							const child = slice.content.firstChild;
							if (!child || child.isInline) return false;

							if (child.isTextblock) {
								// If the slice isn't just text, prose will attempt to put the block into the image strip caption.
								// 	The server would then reject it.
								const sliceWithJustText = new Slice(Fragment.from(child.content), slice.openStart, slice.openEnd);

								tr.replaceRange(selection.from, selection.to, sliceWithJustText);
								tr.setSelection(TextSelection.create(tr.doc, selection.from));
								tr.scrollIntoView();
								view.dispatch(tr);

								return true;
							}
						}

						// Choose the position immediately after the image strip to paste the content
						const $startOfImageStrip = doc.resolve(selection.$from.start(1));
						const insertPos = $startOfImageStrip.after();

						tr.replaceRange(insertPos, insertPos, slice);
						tr.setSelection(TextSelection.create(tr.doc, insertPos));
						tr.scrollIntoView();
						view.dispatch(tr);

						return true;
					},
				},
			}),
		];
	}
}
